/**
 * API utility for making authenticated requests
 */

import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'

const API_BASE = import.meta.env.VITE_V4_API_BASE || 
                 (import.meta.env.VITE_API_BASE ? `${import.meta.env.VITE_API_BASE}/api/v4` : null) ||
                 'http://localhost:5000/api/v4'

let authTokenGetter: (() => Promise<string | null>) | null = null
let orgIdGetter: (() => string | null) | null = null

export function setAuthTokenGetter(getter: () => Promise<string | null>) {
  authTokenGetter = getter
}

export function setOrgIdGetter(getter: () => string | null) {
  orgIdGetter = getter
}

export async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {}
  
  // Get auth token
  if (authTokenGetter) {
    try {
      const token = await authTokenGetter()
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    } catch (error) {
      console.error('Failed to get auth token:', error)
    }
  }
  
  // Get organization ID and add as custom header
  if (orgIdGetter) {
    try {
      const orgId = orgIdGetter()
      if (orgId) {
        headers['X-Organization-ID'] = orgId
      }
    } catch (error) {
      console.error('Failed to get org ID:', error)
    }
  }
  
  return headers
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
})

api.interceptors.request.use(async (config) => {
  const authHeaders = await getAuthHeaders()
  Object.entries(authHeaders).forEach(([key, value]) => {
    config.headers.set(key, value)
  })
  return config
})

export async function apiGet<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const authHeaders = await getAuthHeaders()
  const response = await axios.get(`${API_BASE}${url}`, {
    ...config,
    headers: { ...config?.headers, ...authHeaders },
  })
  return response.data
}

export async function apiPost<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
  const authHeaders = await getAuthHeaders()
  const response = await axios.post(`${API_BASE}${url}`, data, {
    ...config,
    headers: { ...config?.headers, ...authHeaders },
  })
  return response.data
}

export async function apiPut<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
  const authHeaders = await getAuthHeaders()
  const response = await axios.put(`${API_BASE}${url}`, data, {
    ...config,
    headers: { ...config?.headers, ...authHeaders },
  })
  return response.data
}

export async function apiDelete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const authHeaders = await getAuthHeaders()
  const response = await axios.delete(`${API_BASE}${url}`, {
    ...config,
    headers: { ...config?.headers, ...authHeaders },
  })
  return response.data
}

export async function apiUpload<T = any>(
  url: string, 
  formData: FormData, 
  config?: AxiosRequestConfig
): Promise<T> {
  const authHeaders = await getAuthHeaders()
  const response = await axios.post(`${API_BASE}${url}`, formData, {
    ...config,
    headers: { 
      ...config?.headers, 
      ...authHeaders,
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}

export { API_BASE }


